/**
 * Web sibling of ChecklistCardScroll — picked by the web bundler ahead of the
 * mobile file; Metro never sees it.
 *
 * The phone strip is swiped. A mouse cannot swipe a horizontal strip and a
 * wheel scrolls the page, so the web version adds a left and a right arrow
 * that move the strip one card at a time; each hides at its end of the
 * strip. Same cards, same centring of the selected one.
 */
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useEffect, useRef, useState } from 'react';
import {
  Dimensions,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import { RootStackParamList } from '../types/navigation';
import { ms } from '../utils/responsive';

import { CategoryCard } from './CategoryCard';

interface Category {
  id: string;
  name: string;
  icon: any;
  count: number;
  totalTasks: number;
}

interface ChecklistCardScrollProps {
  categories: Category[];
  selectedCategory: string;
  navigation: NativeStackNavigationProp<RootStackParamList>;
  onCategoryPress: (id: string) => void;
}

const CARD_WIDTH = ms(130, 0.3) + ms(15);

export const ChecklistCardScroll: React.FC<ChecklistCardScrollProps> = ({
  categories,
  selectedCategory,
  navigation,
  onCategoryPress,
}) => {
  const scrollRef = useRef<ScrollView>(null);
  const offsetRef = useRef(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [offset, setOffset] = useState(0);

  // Centre the selected card whenever the selection changes (same as the
  // phone; `categories` is left out of the deps on purpose — counts refresh
  // asynchronously and the card order is fixed).
  useEffect(() => {
    const paddingLeft = 20;
    const selectedIdx = categories.findIndex((cat) => cat.id === selectedCategory);
    if (scrollRef.current && selectedIdx !== -1) {
      const width = viewportWidth || Dimensions.get('window').width;
      const target = Math.max(0, CARD_WIDTH * selectedIdx + paddingLeft - width / 2 + CARD_WIDTH / 2);
      // @ts-ignore
      scrollRef.current.scrollTo({ x: target, animated: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const maxOffset = Math.max(0, contentWidth - viewportWidth);
  const canGoLeft = offset > 2;
  const canGoRight = maxOffset > 2 && offset < maxOffset - 2;

  const nudge = (direction: -1 | 1) => {
    const next = Math.min(maxOffset, Math.max(0, offsetRef.current + direction * CARD_WIDTH));
    // @ts-ignore
    scrollRef.current?.scrollTo({ x: next, animated: true });
  };

  const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    offsetRef.current = e.nativeEvent.contentOffset.x;
    setOffset(offsetRef.current);
  };

  return (
    <View style={styles.wrap}>
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.categoriesList}
        contentContainerStyle={styles.content}
        onScroll={onScroll}
        scrollEventThrottle={32}
        onLayout={(e) => setViewportWidth(e.nativeEvent.layout.width)}
        onContentSizeChange={(w) => setContentWidth(w)}
        accessibilityRole="list"
        accessibilityLabel="Task categories"
      >
        {categories.map((item) => (
          <CategoryCard
            key={item.id}
            item={item}
            isSelected={selectedCategory === item.id}
            navigation={navigation}
            onPress={onCategoryPress}
          />
        ))}
      </ScrollView>

      {canGoLeft && (
        <TouchableOpacity
          style={[styles.arrow, styles.arrowLeft]}
          onPress={() => nudge(-1)}
          accessibilityRole="button"
          accessibilityLabel="Previous categories"
        >
          <Text style={styles.arrowText}>‹</Text>
        </TouchableOpacity>
      )}
      {canGoRight && (
        <TouchableOpacity
          style={[styles.arrow, styles.arrowRight]}
          onPress={() => nudge(1)}
          accessibilityRole="button"
          accessibilityLabel="More categories"
        >
          <Text style={styles.arrowText}>›</Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    position: 'relative',
  },
  categoriesList: {
    marginTop: ms(10),
    marginBottom: ms(10),
    minHeight: ms(170, 0.3),
    maxHeight: ms(170, 0.3),
  },
  content: {
    paddingLeft: 20,
    paddingRight: 30,
  },
  arrow: {
    position: 'absolute',
    top: '50%',
    marginTop: -ms(18),
    width: ms(36),
    height: ms(36),
    borderRadius: ms(18),
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#DCE3E8',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#1C2C4F',
    shadowOpacity: 0.12,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    zIndex: 3,
  },
  arrowLeft: {
    left: ms(4),
  },
  arrowRight: {
    right: ms(4),
  },
  arrowText: {
    fontSize: ms(24, 0.3),
    lineHeight: ms(26, 0.3),
    color: '#1C2C4F',
    fontWeight: '600',
    marginTop: -2,
  },
});

export default ChecklistCardScroll;
